import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';

export interface MyDocumentoFatturaRef {
  tipork: string;
  anno: number;
  serie: string;
  numdoc: number;
  numero: string;
  date: string | null;
}

export interface MyDocumento {
  key: { tipork: string; anno: number; serie: string; numdoc: number };
  type: 'fattura' | 'ddt' | 'nota_credito' | string;
  tipoRk: string;        // codice NTS originale (A/B/D/N)
  anno: number;
  serie: string;
  numdoc: number;
  mese: number | null;
  numero: string;
  date: string | null;
  /** null per una Fattura differita appena nata: il totale arriva dal
   *  gestionale poco dopo, e intanto la riga mostra uno skeleton. */
  total: number | null;
  anConto: number;
  /** La modalità di pagamento, quando già letta dal gestionale
   *  (``pagamentoLetto``); altrimenti la legge ``usePagamentiDocumenti``. */
  pagamento: string | null;
  pagamentoLetto: boolean;
  vettor: string | null;
  /** Il documento ha un riferimento di spedizione (fatture immediate e D.D.T.). */
  tracciabile: boolean;
  hasPdf: boolean;
  fattura?: MyDocumentoFatturaRef | null;  // fattura che ha fatturato il D.D.T.
  /** true quando il riferimento fattura non è stato risolvibile perché il
   *  gestionale non risponde. Diverso da ``fattura: null``, che significa
   *  "D.D.T. realmente non ancora fatturato". */
  fattura_non_disponibile?: boolean;
}

export interface MyDocumentiResp {
  page: number;
  page_size: number;
  total: number;
  pages: number;
  items: MyDocumento[];
  available_anni: number[];
  /** Data dell'acquisto più recente che passerebbe gli altri filtri ma non
   *  la finestra ``mesi``. Valorizzata dal backend solo a lista vuota, per
   *  poter spiegare *perché* è vuota. */
  ultimo_documento_date?: string | null;
  /** Soglia effettivamente applicata dalla query (non ricalcolarla lato
   *  client: il backend conta i mesi come 30.44 giorni). */
  finestra_cutoff_date?: string | null;
  /** L'anno mostrato (default il corrente). */
  anno?: number | null;
  /** Il Caricamento dell'anno dal gestionale: assente con ``mesi``. */
  caricamento?: MyDocumentiCaricamento | null;
  /** Solo dai resi: la Finestra di reso, 12 mesi fissi. */
  finestra_mesi?: number;
}

export interface MyDocumentiCaricamento {
  /** ``caricamento``: l'anno non è ancora in cache e sta arrivando;
   *  ``errore``: mai caricato e l'ultimo tentativo è fallito;
   *  ``pronto``: in cache (un aggiornamento può girare dietro). */
  stato: 'caricamento' | 'errore' | 'pronto';
  in_corso: boolean;
  aggiornato_il: string | null;
  ultimo_fallito: boolean;
}

/** Ogni quanto si richiede l'elenco mentre un Caricamento è in corso. */
const POLL_MS = 2000;

export function useMyDocumenti(params: {
  page?: number;
  pageSize?: number;
  tipo?: string;
  anno?: number;
  mese?: number;
  mesi?: number;
  conFattura?: boolean;
  /** Quando false la richiesta non parte e ``loading`` resta true. Serve a chi
   *  deriva ``mesi`` da una configurazione asincrona: senza questo gate la
   *  prima richiesta usa il default e va rifatta appena arriva il valore. */
  enabled?: boolean;
  /** L'elenco da chiedere: i Documenti (default) o quelli da cui si rende
   *  (``/api/clienti/me/rma/documenti``), con la stessa forma. */
  percorso?: string;
} = {}) {
  const { page = 1, pageSize = 25, tipo, anno, mese, mesi, conFattura, enabled = true,
    percorso = '/api/clienti/me/documenti' } = params;
  const [data, setData] = useState<MyDocumentiResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Solo l'ultima richiesta scrive: un polling arrivato dopo un cambio di
  // anno non deve rimettere l'anno vecchio.
  const ultima = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!enabled) return;
    const questa = ++ultima.current;
    // Il polling di un Caricamento non rimette lo spinner: la pagina resta com'è.
    if (!silent) setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('page_size', String(pageSize));
      if (tipo) qs.set('tipo', tipo);
      if (anno) qs.set('anno', String(anno));
      if (mese) qs.set('mese', String(mese));
      if (mesi) qs.set('mesi', String(mesi));
      if (conFattura) qs.set('con_fattura', 'true');
      const { data } = await api.get<MyDocumentiResp>(`${percorso}?${qs}`);
      if (questa === ultima.current) setData(data);
    } catch (e: any) {
      if (questa === ultima.current) setError(e?.response?.data?.detail ?? 'Errore caricamento documenti');
    } finally {
      if (questa === ultima.current) setLoading(false);
    }
  }, [page, pageSize, tipo, anno, mese, mesi, conFattura, enabled, percorso]);

  useEffect(() => { load(); }, [load]);

  // Mentre il gestionale porta l'anno (o lo aggiorna) si richiede l'elenco
  // finché il Caricamento non finisce.
  const caricando = !!data?.caricamento && (data.caricamento.stato === 'caricamento' || data.caricamento.in_corso);
  useEffect(() => {
    if (!caricando) return;
    const t = setTimeout(() => { load(true); }, POLL_MS);
    return () => clearTimeout(t);
  }, [caricando, data, load]);

  return { data, loading, error, reload: () => load() };
}

export interface MyDocumentoKey {
  tipork: string;
  anno: number;
  serie: string;
  numdoc: number;
}

export interface MyDocumentoOrdineOrigine {
  tipork: string;
  anno: number;
  serie: string;
  numdoc: number;
  /** Serie RMA: la pratica, la cui pagina mostra l'Ordine RMA (INT-133). */
  rma?: string | null;
}

export interface MyDocumentoItem {
  riga: number;
  sku: string;
  title: string;
  quantita: number;
  unmis: string;
  prezzoUnitario: number;
  totaleRiga: number;
  scont1: number;
  scont2: number;
  ordineOrigine: MyDocumentoOrdineOrigine | null;
  /** Solo nelle righe di una Fattura differita. */
  documento?: MyDocumentoOrigine;
}

export interface MyDocumentoDetail extends MyDocumento {
  items: MyDocumentoItem[];
}

/** Il D.D.T. da cui viene una riga di Fattura differita: il reso punta lì. */
export interface MyDocumentoOrigine extends MyDocumentoKey {
  numero: string;
}
export function useMyDocumentoDetail(key: MyDocumentoKey | null) {
  const [data, setData] = useState<MyDocumentoDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!key) { setData(null); return; }
    setLoading(true);
    setError(null);
    const { tipork, anno, serie, numdoc } = key;
    api
      .get<MyDocumentoDetail>(`/api/clienti/me/documenti/${tipork}/${anno}/${encodeURIComponent(serie)}/${numdoc}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail ?? 'Errore caricamento dettaglio'))
      .finally(() => setLoading(false));
  }, [key]);

  return { data, loading, error };
}

export interface MyScadenza {
  id: string;
  annoPartita: number;
  numeroPartita: number;
  rata: number;
  scadenza: string | null;
  dataDocumento: string | null;
  descrizione: string | null;
  importo: number;
  saldo: number;
  stato: 'aperta' | 'scaduta' | 'pagata';
  darave: string | null;
}

export interface MyScadenzarioResp {
  page: number;
  count: number;
  totaleAperto: number;
  totaleScaduto: number;
  items: MyScadenza[];
}

export function useMyScadenzario(params: { page?: number } = {}) {
  const { page = 0 } = params;
  const [data, setData] = useState<MyScadenzarioResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<MyScadenzarioResp>(`/api/clienti/me/scadenzario?page=${page}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail ?? 'Errore caricamento scadenzario'))
      .finally(() => setLoading(false));
  }, [page]);

  return { data, loading, error };
}

const chiaveDoc = (k: MyDocumento['key']) => `${k.tipork}-${k.anno}-${k.serie}-${k.numdoc}`;

/**
 * La modalità di pagamento dei documenti di una pagina dell'elenco, che
 * l'elenco di NTS non rende: il backend la legge con ``DocumentiApri`` per
 * quelli che non l'hanno ancora e la salva (collaudo del 03/11/2026, #13).
 * Rende ``pagamento(doc)``: il testo, ``null`` se il documento non ne ha, o
 * ``undefined`` mentre arriva.
 */
export function usePagamentiDocumenti(items: MyDocumento[]) {
  const [letti, setLetti] = useState<Record<string, string | null>>({});
  const daLeggere = items.filter((d) => !d.pagamentoLetto);
  const firma = daLeggere.map((d) => chiaveDoc(d.key)).join('|');

  useEffect(() => {
    if (!daLeggere.length) return;
    let attivo = true;
    api
      .post<{ items: { key: MyDocumento['key']; pagamento: string | null; letto: boolean }[] }>(
        '/api/clienti/me/documenti/pagamenti', { documenti: daLeggere.map((d) => d.key) })
      .then((r) => {
        if (!attivo) return;
        setLetti((prima) => {
          const dopo = { ...prima };
          for (const i of r.data.items) dopo[chiaveDoc(i.key)] = i.letto ? i.pagamento : null;
          return dopo;
        });
      })
      .catch(() => {
        // Il gestionale non risponde: la colonna resta "—", come prima.
        if (!attivo) return;
        setLetti((prima) => {
          const dopo = { ...prima };
          for (const d of daLeggere) dopo[chiaveDoc(d.key)] = null;
          return dopo;
        });
      });
    return () => { attivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma]);

  return useCallback((doc: MyDocumento): string | null | undefined => {
    if (doc.pagamentoLetto) return doc.pagamento;
    const k = chiaveDoc(doc.key);
    return k in letti ? letti[k] : undefined;
  }, [letti]);
}
